import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { useAuth } from '../lib/auth/AuthContext'
import { t } from '../lib/i18n'
import { Link } from 'react-router-dom'

const loginSchema = z.object({
  email: z.string().min(1, t().auth.required).email(t().auth.invalidEmail),
  password: z.string().min(6, t().auth.passwordMin),
})

type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const { user, isLoading, login } = useAuth()
  const navigate = useNavigate()
  const strings = t()

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, isLoading, navigate])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data.email, data.password)
      navigate('/dashboard', { replace: true })
    } catch {
      setError('root', { message: strings.auth.loginError })
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">{strings.general.loading}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{strings.brand.name}</h1>
          <p className="text-sm text-gray-500 mt-1">{strings.brand.tagline}</p>
        </div>

        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-6">{strings.auth.login}</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label={strings.auth.email}
              type="email"
              autoComplete="email"
              placeholder={strings.auth.emailPlaceholder}
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label={strings.auth.password}
              type="password"
              autoComplete="current-password"
              placeholder={strings.auth.passwordPlaceholder}
              error={errors.password?.message}
              {...register('password')}
            />

            {errors.root && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {errors.root.message}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              isLoading={isSubmitting}
            >
              {isSubmitting ? strings.auth.loggingIn : strings.auth.loginButton}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {strings.auth.noAccountYet}{' '}
              <Link
                to="/signup"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                {strings.auth.createAccountLink}
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  )
}
